import { useState, useEffect } from 'react'
import { siteAPI, uploadPdf } from '../services/api'
import { useNotification } from '../context/NotificationContext'
import { getErrorMessage, getImageUrl, confirmAction } from '../utils/helpers'

const AllServices = () => {
  const { showSuccess, showError } = useNotification()
  const [pdfs, setPdfs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchPdfs()
  }, [])

  const fetchPdfs = async () => {
    try {
      const response = await siteAPI.getSettings()
      setPdfs(response.data.settings?.service_pdfs || [])
    } catch (error) {
      showError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const handleUpload = async (e) => {
    e.preventDefault()
    const formData = new FormData(e.target)
    const files = formData.getAll('pdfs')
    
    if (files.length === 0 || (files.length === 1 && files[0].size === 0)) {
      showError('Choose at least one PDF file to upload.')
      return
    }

    if (pdfs.length + files.length > 6) {
      showError('You can only have up to 6 PDF files. Remove some first.')
      return
    }

    try {
      // Upload PDFs to upload server
      const uploadRes = await uploadPdf(files)
      const uploadedPdfs = uploadRes.data.pdfs // Array of {file, title} objects
      
      // Merge with existing PDFs
      const newPdfs = [...uploadedPdfs, ...pdfs]
      await siteAPI.updateSettings({ service_pdfs: newPdfs })
      
      showSuccess(`${files.length} PDF(s) uploaded successfully.`)
      fetchPdfs()
      e.target.reset()
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  const handleDelete = async (pdfPath) => {
    if (!confirmAction('Delete this PDF?')) return
    
    try {
      const newPdfs = pdfs.filter(pdf => pdf !== pdfPath)
      await siteAPI.updateSettings({ service_pdfs: newPdfs })
      showSuccess('PDF deleted.')
      fetchPdfs()
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  if (loading) return <div className="admin-page">Loading...</div>

  return (
    <div className="admin-page">
      <h2 className="admin-title">All Services</h2>
      <p className="admin-sub">Manage PDF service menus (maximum 6 files).</p>

      <div className="row">
        <div className="col-lg-8">
          <div className="panel">
            <div className="panel-head">Uploaded PDF Files</div>
            {pdfs.length > 0 ? (
              pdfs.map((pdf, index) => {
                // Handle both object format {file, title} and string format
                const pdfFile = typeof pdf === 'object' ? pdf.file : pdf
                const pdfTitle = typeof pdf === 'object' ? pdf.title : (pdf || '').replace('uploads/', '')
                
                return (
                  <div key={index} className="item-row">
                    <div className="item-info">
                      <div className="item-name">
                        <i className="fa fa-file-pdf-o" style={{marginRight: '8px', color: 'var(--gold-3)'}}></i>
                        {pdfTitle}
                      </div>
                      <div className="item-sub">
                        <a href={getImageUrl(pdfFile)} target="_blank" rel="noopener noreferrer">View PDF →</a>
                      </div>
                    </div>
                    <button onClick={() => handleDelete(pdfFile)} className="btn btn-sm btn-danger">
                      <i className="fa fa-trash"></i> Delete
                    </button>
                  </div>
                )
              })
            ) : (
              <p className="muted">No PDF files uploaded yet.</p>
            )}
            <p className="muted mt-3" style={{fontSize: '12px'}}>
              {pdfs.length} of 6 slots used
            </p>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="panel">
            <div className="panel-head">Upload PDF Files</div>
            <form onSubmit={handleUpload}>
              <div className="form-group">
                <label>PDF Files * (up to 6 total)</label>
                <input 
                  type="file" 
                  name="pdfs" 
                  className="form-control-file" 
                  accept=".pdf,application/pdf" 
                  multiple 
                  required 
                />
              </div>
              <p className="muted" style={{fontSize: '12px'}}>
                Upload service menu PDFs. Each file should be under 25 MB.
              </p>
              <button className="btn btn-gold btn-block" disabled={pdfs.length >= 6}>
                {pdfs.length >= 6 ? 'Maximum Reached' : 'Upload PDF Files'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AllServices
